import { BoxType } from "notifications/notification-box-type";
import z from "zod";

const count = z.number().int().nonnegative();

/** The API's box names, as `NotificationBoxType` spells them, by the page's own. */
const boxNames: Readonly<Record<BoxType, string>> = {
  [BoxType.Inbox]: "inbox",
  [BoxType.Outbox]: "outbox",
  [BoxType.Forums]: "forums",
  [BoxType.Archive]: "archive",
};

/**
 * The counts the Communications page shows for the reader's notifications, from
 * `GET /notifications/counts` (#6709): the list, each box, and the Unread and Action required chips.
 * The boxes honor the list's criteria and not its chips, so they count the Sent and Archived chips
 * whether or not those are on (#6719).
 *
 * The boxes arrive keyed by the API's enum names. They are read without regard to case, since the
 * serializer decides whether a dictionary key is camel-cased.
 */
export const NotificationListCountsJSONZ = z.object({
  total: count,
  boxes: z.record(z.string(), count),
  unread: count,
  actionRequired: count,
});

export type NotificationListCountsJSON = z.infer<typeof NotificationListCountsJSONZ>;

export type NotificationListCounts = Readonly<{
  total: number;
  boxes: Readonly<Record<BoxType, number>>;
  unread: number;
  actionRequired: number;
}>;

/** The counts with each box under the page's own name, and a box the API left out as none. */
export function toNotificationListCounts(json: NotificationListCountsJSON): NotificationListCounts {
  const byName = new Map(Object.entries(json.boxes).map(([name, value]) => [name.toLowerCase(), value]));
  const box = (boxType: BoxType) => byName.get(boxNames[boxType]) ?? 0;
  return {
    total: json.total,
    boxes: {
      [BoxType.Inbox]: box(BoxType.Inbox),
      [BoxType.Outbox]: box(BoxType.Outbox),
      [BoxType.Forums]: box(BoxType.Forums),
      [BoxType.Archive]: box(BoxType.Archive),
    },
    unread: json.unread,
    actionRequired: json.actionRequired,
  };
}
